import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, setAuthToken, getAuthToken } from "../lib/api";

const AuthContext = createContext({ user: null, loading: true, refresh: () => {}, logout: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getAuthToken()) {
      setUser(null); setLoading(false); return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("[Auth] logout request failed (continuing anyway)", err);
    }
    // Clear the Bearer token we persisted on login so subsequent app loads
    // start unauthenticated (the cookie-clear endpoint can't reach us anyway
    // because of the preview ingress CORS-credentials limitation).
    setAuthToken(null);
    setUser(null);
    window.location.href = "/";
  }, []);

  const value = useMemo(() => ({ user, loading, refresh, setUser, logout }), [user, loading, refresh, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
